import React, { useEffect } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  useColorMode,
  List,
  ListItem,
  ListIcon,
  Flex,
  Badge,
  Button,
} from '@chakra-ui/react'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  FaCheckCircle,
  FaRocket,
  FaClock,
  FaShieldAlt,
  FaWhatsapp,
} from 'react-icons/fa'
import LEColorConstants, {
  basicTextColor,
} from '@/lib/constants/colorConstants'
import CustomButton from '../Button'
import router from 'next/router'

export const FinalCTA = () => {
  const { colorMode } = useColorMode()
  const bgColor = useColorModeValue('white', 'gray.900')

  const controls = useAnimation()
  const { inView, ref } = useInView({ threshold: 0.3 })

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  const goToSignUp = () => {
    const host =
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
        ? process.env.NEXT_PUBLIC_APP_HOST
        : process.env.NEXT_PUBLIC_APP_LOCALHOST

    return router.push(host + '/signup')
  }

  const guarantees = [
    'No setup fees or hidden costs',
    '500 credits completely FREE',
    '24/7 dedicated support',
    '30-day money-back guarantee',
    'Instant platform access',
    'Free onboarding & training',
  ]

  const urgencyPoints = [
    'WAEC & NECO CBT transition by 2026',
    'Only 23 spots left in this offer',
    'Competitors are already preparing',
    'Every day counts for student preparation',
  ]

  return (
    <Box
      bg={bgColor}
      py="5rem"
      ref={ref}
      as={motion.div}
      animate={controls}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      position="relative"
      overflow="hidden"
    >
      {/* Background Elements */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgImage="radial-gradient(circle at 20% 80%, rgba(0, 155, 255, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 110, 6, 0.15) 0%, transparent 50%)"
        zIndex="0"
      />

      <Container
        maxW="90rem"
        mx="auto"
        px={['1rem', '3rem', '4rem', '6rem']}
        position="relative"
        zIndex="1"
      >
        <VStack spacing="4rem">
          {/* Final Urgency Header */}
          <Box
            textAlign="center"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 50 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            <Badge
              colorScheme="red"
              variant="solid"
              fontSize="1.1rem"
              py="0.8rem"
              px="2rem"
              borderRadius="full"
              mb="2rem"
              animation="pulse 2s infinite"
            >
              🚨 LAST CHANCE - DON'T MISS OUT!
            </Badge>

            <Heading
              fontSize={['2.5rem', '3rem', '4rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="1rem"
              lineHeight="1.2"
            >
              Your Students'{' '}
              <Text as="span" color={LEColorConstants.secondary}>
                Future
              </Text>{' '}
              Depends on{' '}
              <Text as="span" color={LEColorConstants.primary}>
                This Decision
              </Text>
            </Heading>

            <Text
              fontSize={['1.2rem', '1.4rem', '1.6rem']}
              color={basicTextColor[colorMode]}
              maxW="5xl"
              mx="auto"
              fontWeight="medium"
              lineHeight="1.5"
            >
              While other schools scramble in 2026, your students will be
              confident, prepared, and ready to excel. The CBT revolution is
              happening NOW.
            </Text>
          </Box>

          {/* Main CTA Container */}
          <Box
            w="full"
            maxW="6xl"
            bg="linear-gradient(135deg, #009BFF 0%, #FF6E06 100%)"
            borderRadius="24px"
            p={['2.5rem', '3rem', '4rem']}
            textAlign="center"
            position="relative"
            overflow="hidden"
            boxShadow="2xl"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, scale: 0.95 },
              visible: {
                opacity: 1,
                scale: 1,
                transition: { duration: 0.8, delay: 0.2 },
              },
            }}
          >
            {/* Animated Background Elements */}
            <Box
              position="absolute"
              top="-100px"
              right="-100px"
              w="300px"
              h="300px"
              borderRadius="full"
              bg="rgba(255, 255, 255, 0.1)"
              as={motion.div}
              animate={
                {
                  scale: [1, 1.2, 1],
                  rotate: [0, 180, 360],
                } as any
              }
              transition={
                {
                  duration: 20,
                  repeat: Infinity,
                  ease: 'linear',
                } as any
              }
            />

            <VStack spacing="3rem" position="relative" zIndex="1">
              <VStack spacing="1.5rem">
                <Heading
                  fontSize={['2rem', '2.5rem', '3rem']}
                  fontWeight="bold"
                  color="white"
                  lineHeight="1.2"
                >
                  Get Started Today with
                  <br />
                  500 FREE Credits!
                </Heading>

                <Text
                  fontSize={['1.2rem', '1.4rem']}
                  color="rgba(255, 255, 255, 0.9)"
                  maxW="4xl"
                  lineHeight="1.6"
                >
                  Worth ₦25,000 - Everything you need to test your entire school
                  and see the dramatic improvement in your students'
                  performance.
                </Text>
              </VStack>

              {/* What They Get */}
              <Flex
                direction={{ base: 'column', md: 'row' }}
                gap="2rem"
                w="full"
                maxW="5xl"
              >
                <Box
                  flex="1"
                  bg="rgba(255, 255, 255, 0.15)"
                  borderRadius="16px"
                  p="2rem"
                  backdropFilter="blur(10px)"
                  textAlign="left"
                >
                  <Heading
                    fontSize="1.5rem"
                    color="white"
                    mb="1rem"
                    textAlign="center"
                  >
                    ✅ What You Get Immediately:
                  </Heading>

                  <List spacing="1rem">
                    {guarantees.map((guarantee, index) => (
                      <ListItem key={index} display="flex" alignItems="center">
                        <ListIcon
                          as={FaCheckCircle}
                          color="white"
                          boxSize="18px"
                        />
                        <Text color="white" fontSize="1rem">
                          {guarantee}
                        </Text>
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box
                  flex="1"
                  bg="rgba(239, 68, 68, 0.2)"
                  borderRadius="16px"
                  p="2rem"
                  backdropFilter="blur(10px)"
                  textAlign="left"
                  border="2px solid rgba(239, 68, 68, 0.5)"
                >
                  <Heading
                    fontSize="1.5rem"
                    color="white"
                    mb="1rem"
                    textAlign="center"
                  >
                    ⚠️ Why You Must Act NOW:
                  </Heading>

                  <List spacing="1rem">
                    {urgencyPoints.map((point, index) => (
                      <ListItem key={index} display="flex" alignItems="center">
                        <ListIcon as={FaClock} color="#FEF3C7" boxSize="18px" />
                        <Text color="white" fontSize="1rem" fontWeight="medium">
                          {point}
                        </Text>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Flex>

              {/* Main CTA Button */}
              <VStack spacing="1.5rem">
                <CustomButton
                  size="lg"
                  w={{ base: 'full', sm: 'auto' }}
                  borderRadius="16px"
                  fontWeight="bold"
                  fontSize={['0.9rem', '1.2rem']}
                  //   bg="white"
                  //   color={LEColorConstants.primary}
                  onClick={goToSignUp}
                  _hover={{
                    transform: 'translateY(-4px) scale(1.05)',
                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
                    // bg: 'gray.50',
                  }}
                  _active={{
                    transform: 'translateY(-2px) scale(1.02)',
                  }}
                  transition="all 0.2s"
                  boxShadow="0 15px 35px rgba(0, 0, 0, 0.2)"
                >
                  🚀 CLAIM YOUR 500 FREE CREDITS NOW!
                </CustomButton>

                <Text
                  fontSize="1rem"
                  color="rgba(255, 255, 255, 0.8)"
                  textAlign="center"
                >
                  ✅ No Credit Card Required • ✅ Instant Access • ✅ Risk-Free
                  Trial
                </Text>

                <HStack spacing="2rem" flexWrap="wrap" justify="center">
                  <HStack>
                    <FaShieldAlt color="white" />
                    <Text color="white" fontSize="0.9rem">
                      SSL Secured
                    </Text>
                  </HStack>
                  <HStack>
                    <FaRocket color="white" />
                    <Text color="white" fontSize="0.9rem">
                      Instant Setup
                    </Text>
                  </HStack>
                  <HStack>
                    <FaCheckCircle color="white" />
                    <Text color="white" fontSize="0.9rem">
                      Trusted by 200+ Schools
                    </Text>
                  </HStack>
                </HStack>
              </VStack>
            </VStack>
          </Box>

          {/* Final Warning */}
          <Box
            textAlign="center"
            maxW="4xl"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.6 },
              },
            }}
          >
            <Text
              fontSize={['1.3rem', '1.5rem']}
              fontWeight="bold"
              color={LEColorConstants.secondary}
              mb="1rem"
            >
              ⏰ This Offer Expires When 100 Schools Claim It
            </Text>

            <Text
              fontSize={['1.1rem', '1.2rem']}
              color={basicTextColor[colorMode]}
              mb="1.5rem"
              lineHeight="1.6"
            >
              We can only provide this level of support and free credits to a
              limited number of schools.
              <Text
                as="span"
                fontWeight="bold"
                color={LEColorConstants.primary}
              >
                {' '}
                Only 23 spots remain.
              </Text>
            </Text>

            <Text
              fontSize={['1rem', '1.1rem']}
              color="gray.600"
              fontStyle="italic"
            >
              Join the smart schools that are already preparing their students
              for the CBT future. Your competitors are signing up while you read
              this.
            </Text>
          </Box>

          {/* Contact Info */}
          <Box
            textAlign="center"
            p="2rem"
            bg={useColorModeValue('gray.100', 'gray.800')}
            borderRadius="12px"
            w="full"
            maxW="3xl"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.8 },
              },
            }}
          >
            <Text
              fontSize="1.1rem"
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="0.5rem"
            >
              Questions? We're Here to Help!
            </Text>
            <Text fontSize="1rem" color="gray.600">
              📞 Call us directly: +234 (81) 411 15 717
              <br />
              📧 Email: info@testnotion.com
              <br />
              💬 Live chat available 24/7
            </Text>
            <Button
              onClick={() =>
                window.open(
                  'https://wa.me/2348141115717?text=I%20have%20a%20question%20about%20the%20500%20free%20credits',
                  '_blank'
                )
              }
              bg="green"
              color="white"
              borderRadius="8px"
              p="0.5rem 1rem"
              mt="1rem"
              fontSize="1rem"
              fontWeight="bold"
              _hover={{
                bg: 'green.600',
              }}
              _active={{
                bg: 'green.700',
              }}
              transition="all 0.2s"
            >
              <FaWhatsapp /> &nbsp; WhatsApp Now
            </Button>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
